import { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  canViewAppActivity,
  canViewQueries,
  deleteAdminProfilePicture,
  getAdminDesignation,
  getAdminPassword,
  ADMIN_PROFILE_PHOTO_UPDATED_EVENT,
  getAdminProfilePicture,
  getAdminUser,
  logoutAdmin,
  saveAdminProfilePicture
} from "../libraries/auth";
import { canAccessNavRoute, canViewDatabaseSheetsTab } from "../libraries/privileges";
import { getAllBranchesURL, getAllComplaintsURL, getUpdateLicenseRequestsByBranchURL } from "../libraries/apiUrl";
import { getActivityByName } from "../libraries/activityLogger";
import {
  describeLicenseRequestNotifications,
  diffBranchLicenseNotifications,
  extractLicenseRequestSnapshots,
  extractNotificationBranches,
  getLicenseNotificationState,
  isLicenseRequestNotificationRecipient,
  playArrivalNotificationTone,
  saveLicenseNotificationState,
  showBrowserArrivalNotification
} from "../libraries/arrivalNotifications";
import { _post, _get } from "../libraries/fetch";
import { saveLoggedInAdminProfileImage } from "../libraries/tecgymAdminUsers";

const QUERY_PAYLOAD = {};
const QUERY_POLL_INTERVAL_MS = 60 * 1000;
const LICENSE_REQUEST_POLL_INTERVAL_MS = 60 * 1000;
const LICENSE_REQUEST_POLL_BATCH_SIZE = 8;
const PENDING_QUERY_STORAGE_KEY = "tecgymPendingQueryKeys";
let licenseRequestPollInFlight = false;

const WEB_APP_ROUTES = [
  "/web-dashboard",
  "/branches",
  "/queries",
  "/tecfolk-activity"
];
const MOBILE_APP_ROUTES = ["/mobile-dashboard", "/feature-requests", "/mobile-clients"];
const EMPLOYEE_MANAGEMENT_ROUTES = ["/employee-management", "/app-activity"];
const DATABASE_SHEETS_ROUTES = ["/database-sheets", "/contact-us", "/login-logs"];

function Header() {
    const navigate = useNavigate();
    const location = useLocation();
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [isPhotoOptionsOpen, setIsPhotoOptionsOpen] = useState(false);
    const [isPhotoViewOpen, setIsPhotoViewOpen] = useState(false);
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const [profilePicture, setProfilePicture] = useState("");
    const [profilePictureError, setProfilePictureError] = useState("");
    const [cropImage, setCropImage] = useState("");
    const [cropZoom, setCropZoom] = useState(1);
    const [cropX, setCropX] = useState(50);
    const [cropY, setCropY] = useState(50);
    const [pendingQueriesCount, setPendingQueriesCount] = useState(0);
    const [queryNotification, setQueryNotification] = useState("");
    const [licenseNotification, setLicenseNotification] = useState(null);
    const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
    const [isWebAppOpen, setIsWebAppOpen] = useState(false);
    const [isMobileAppOpen, setIsMobileAppOpen] = useState(false);
    const [isEmployeeManagementOpen, setIsEmployeeManagementOpen] = useState(false);
    const [isDatabaseSheetsOpen, setIsDatabaseSheetsOpen] = useState(false);
    const profileMenuRef = useRef(null);
    const webAppMenuRef = useRef(null);
    const mobileAppMenuRef = useRef(null);
    const employeeManagementMenuRef = useRef(null);
    const databaseSheetsMenuRef = useRef(null);
    const profilePictureInputRef = useRef(null);
    const queryNotificationTimerRef = useRef(null);
    const licenseNotificationTimerRef = useRef(null);
    const notificationPermissionAskedRef = useRef(false);
    const licenseNotificationBranchIdRef = useRef("");
    const adminUser = getAdminUser();
    const adminEmail = adminUser?.email || "tecgymadmin@demogymf11.com";
    const adminName = adminUser?.name || "Admin";
    const adminDesignation = getAdminDesignation(adminUser);
    const adminPassword = getAdminPassword();
    const showAppActivity = canViewAppActivity(adminUser);
    const showQueries = canViewQueries(adminUser);
    const showTecfolkActivity = canAccessNavRoute("/tecfolk-activity");
    const showPublished = canAccessNavRoute("/published");
    const showDatabaseSheets = canAccessNavRoute("/database-sheets");
    const showVendorSheet = canViewDatabaseSheetsTab("vendor");
    const showTechnicianSheet = canViewDatabaseSheetsTab("technician");
    const showContactUsSheet = canViewDatabaseSheetsTab("contact-us");
    const showLoginLogsSheet = canViewDatabaseSheetsTab("login-logs");
    const showDatabaseSheetsMenu = showDatabaseSheets
      || showVendorSheet
      || showTechnicianSheet
      || showContactUsSheet
      || showLoginLogsSheet;
    const showDashboard = canAccessNavRoute("/dashboard");
    const showWebDashboard = canAccessNavRoute("/web-dashboard");
    const showEmployeeManagement = canAccessNavRoute("/employee-management");
    const showClients = canAccessNavRoute("/branches");
    const showFeatureRequests = canAccessNavRoute("/feature-requests");
    const showMobileAppClients = canAccessNavRoute("/mobile-clients");
    const showMobileDashboard = canAccessNavRoute("/mobile-dashboard");
    const showMobileApp = showFeatureRequests || showMobileAppClients || showMobileDashboard;
    const showEmployeeManagementMenu = showEmployeeManagement || showAppActivity;
    const showWebApp = showWebDashboard || showClients || showQueries || showTecfolkActivity;
    const isMobileClientsDetails =
      location.pathname.startsWith("/branches/") && location.state?.clientsSource === "mobile";
    const isWebAppRouteActive = !isMobileClientsDetails && WEB_APP_ROUTES.some(
      (route) => location.pathname === route || location.pathname.startsWith(`${route}/`)
    );
    const isMobileAppRouteActive = isMobileClientsDetails || MOBILE_APP_ROUTES.some(
      (route) => location.pathname === route || location.pathname.startsWith(`${route}/`)
    );
    const isEmployeeManagementRouteActive = EMPLOYEE_MANAGEMENT_ROUTES.some(
      (route) => location.pathname === route || location.pathname.startsWith(`${route}/`)
    );
    const isDatabaseSheetsRouteActive = DATABASE_SHEETS_ROUTES.some(
      (route) => location.pathname === route || location.pathname.startsWith(`${route}/`)
    );
    const avatarInitial = (adminName || adminEmail).charAt(0).toUpperCase();

    useEffect(() => {
      const refreshProfilePicture = () => {
        setProfilePicture(getAdminProfilePicture(adminEmail) || "");
      };

      refreshProfilePicture();
      window.addEventListener(ADMIN_PROFILE_PHOTO_UPDATED_EVENT, refreshProfilePicture);
      return () => window.removeEventListener(ADMIN_PROFILE_PHOTO_UPDATED_EVENT, refreshProfilePicture);
    }, [adminEmail]);

    const openQueriesPage = useCallback(() => {
      navigate("/queries");
    }, [navigate]);

    const showQueryArrivalNotification = useCallback(() => {
      setQueryNotification("New query arrived.");
      playQueryNotificationTone();

      if (queryNotificationTimerRef.current) {
        window.clearTimeout(queryNotificationTimerRef.current);
      }

      queryNotificationTimerRef.current = window.setTimeout(() => {
        setQueryNotification("");
      }, 6000);

      if (!("Notification" in window)) {
        return;
      }

      if (window.Notification.permission === "granted") {
        const notification = new window.Notification("Query", { body: "New query arrived." });
        notification.onclick = () => {
          window.focus();
          openQueriesPage();
          notification.close();
        };
        return;
      }

      if (window.Notification.permission === "default" && !notificationPermissionAskedRef.current) {
        notificationPermissionAskedRef.current = true;
        window.Notification.requestPermission().then((permission) => {
          if (permission === "granted") {
            const notification = new window.Notification("Query", { body: "New query arrived." });
            notification.onclick = () => {
              window.focus();
              openQueriesPage();
              notification.close();
            };
          }
        });
      }
    }, [openQueriesPage]);

    const openLicenseRequest = useCallback((branchId) => {
      const normalizedBranchId = String(branchId || licenseNotificationBranchIdRef.current || "").trim();

      if (!normalizedBranchId) {
        return;
      }

      navigate(`/branches/${normalizedBranchId}`, {
        state: {
          branchId: normalizedBranchId,
          activeRoute: "UpdateLicense"
        }
      });
    }, [navigate]);

    const showLicenseRequestNotification = useCallback((summary) => {
      const body = String(summary?.body || "").trim();

      if (!body) {
        return;
      }

      licenseNotificationBranchIdRef.current = String(summary?.branchId || "");
      setLicenseNotification({
        body,
        branchId: licenseNotificationBranchIdRef.current
      });
      playArrivalNotificationTone();

      if (licenseNotificationTimerRef.current) {
        window.clearTimeout(licenseNotificationTimerRef.current);
      }

      licenseNotificationTimerRef.current = window.setTimeout(() => {
        setLicenseNotification(null);
      }, 6000);

      showBrowserArrivalNotification({
        title: "License Update Request",
        body,
        onClick: () => openLicenseRequest(summary?.branchId),
        permissionAskedRef: notificationPermissionAskedRef
      });
    }, [openLicenseRequest]);

    useEffect(() => {
      return () => {
        if (queryNotificationTimerRef.current) {
          window.clearTimeout(queryNotificationTimerRef.current);
        }

        if (licenseNotificationTimerRef.current) {
          window.clearTimeout(licenseNotificationTimerRef.current);
        }
      };
    }, []);

    useEffect(() => {
      const loggedInName = getActivityByName() || adminName;

      if (!isLicenseRequestNotificationRecipient(loggedInName)) {
        return undefined;
      }

      let isMounted = true;

      const pollLicenseRequests = async () => {
        if (licenseRequestPollInFlight) {
          return;
        }

        licenseRequestPollInFlight = true;

        try {
          const branchResponse = await _get(getAllBranchesURL(), { useCache: false });
          const branches = extractNotificationBranches(branchResponse);

          if (!isMounted || branches.length === 0) {
            return;
          }

          let state = getLicenseNotificationState();
          const messages = [];

          for (let index = 0; index < branches.length; index += LICENSE_REQUEST_POLL_BATCH_SIZE) {
            const batch = branches.slice(index, index + LICENSE_REQUEST_POLL_BATCH_SIZE);
            const results = await Promise.all(batch.map(async (branch) => {
              try {
                const response = await _get(getUpdateLicenseRequestsByBranchURL(branch.id), {
                  useCache: false
                });

                if (response === null || response?.success === false || response?.status === false) {
                  return null;
                }

                return {
                  branch,
                  snapshots: extractLicenseRequestSnapshots(response)
                };
              } catch {
                return null;
              }
            }));

            for (const result of results) {
              if (!result) {
                continue;
              }

              const diff = diffBranchLicenseNotifications(
                state,
                result.branch.id,
                result.branch.name,
                result.snapshots
              );
              state = diff.nextState;
              messages.push(...diff.messages);
            }
          }

          saveLicenseNotificationState(state);

          if (isMounted && messages.length > 0) {
            showLicenseRequestNotification(describeLicenseRequestNotifications(messages));
          }
        } catch {
          // Keep the current notification baseline if a background poll fails.
        } finally {
          licenseRequestPollInFlight = false;
        }
      };

      pollLicenseRequests();
      const intervalId = window.setInterval(pollLicenseRequests, LICENSE_REQUEST_POLL_INTERVAL_MS);

      return () => {
        isMounted = false;
        window.clearInterval(intervalId);
      };
    }, [adminName, showLicenseRequestNotification]);

    useEffect(() => {
      if (!showQueries) {
        setPendingQueriesCount(0);
        return undefined;
      }

      let isMounted = true;

      const fetchPendingQueries = async () => {
        try {
          const response = await _get(getAllComplaintsURL(), QUERY_PAYLOAD);

          if (response?.success === false) {
            return;
          }

          const pendingQueries = extractQueryItems(response).filter(isPendingQuery);
          const pendingQueryKeys = [
            ...new Set(pendingQueries.map(getQueryKey).filter(Boolean))
          ];
          const previousPendingQueryKeys = getStoredPendingQueryKeys();

          if (!isMounted) {
            return;
          }

          setPendingQueriesCount(pendingQueries.length);

          if (
            Array.isArray(previousPendingQueryKeys) &&
            pendingQueryKeys.some((queryKey) => !previousPendingQueryKeys.includes(queryKey))
          ) {
            showQueryArrivalNotification();
          }

          savePendingQueryKeys(pendingQueryKeys);
        } catch {
          // Keep the current badge if a background polling request fails.
        }
      };

      fetchPendingQueries();
      const intervalId = window.setInterval(fetchPendingQueries, QUERY_POLL_INTERVAL_MS);

      return () => {
        isMounted = false;
        window.clearInterval(intervalId);
      };
    }, [showQueries, showQueryArrivalNotification]);

    useEffect(() => {
      const handleOutsideClick = (event) => {
        if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
          setIsProfileOpen(false);
          setIsPhotoOptionsOpen(false);
          setIsPhotoViewOpen(false);
          setCropImage("");
        }

        if (webAppMenuRef.current && !webAppMenuRef.current.contains(event.target)) {
          setIsWebAppOpen(false);
        }

        if (mobileAppMenuRef.current && !mobileAppMenuRef.current.contains(event.target)) {
          setIsMobileAppOpen(false);
        }

        if (employeeManagementMenuRef.current && !employeeManagementMenuRef.current.contains(event.target)) {
          setIsEmployeeManagementOpen(false);
        }

        if (databaseSheetsMenuRef.current && !databaseSheetsMenuRef.current.contains(event.target)) {
          setIsDatabaseSheetsOpen(false);
        }
      };

      document.addEventListener("mousedown", handleOutsideClick);

      return () => {
        document.removeEventListener("mousedown", handleOutsideClick);
      };
    }, []);

    useEffect(() => {
      setIsWebAppOpen(false);
      setIsMobileAppOpen(false);
      setIsEmployeeManagementOpen(false);
      setIsDatabaseSheetsOpen(false);
    }, [location.pathname]);

    useEffect(() => {
      const handleResize = () => {
        if (window.innerWidth > 900) {
          setIsMobileNavOpen(false);
        }
      };

      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
      if (!isMobileNavOpen) {
        return undefined;
      }

      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";

      return () => {
        document.body.style.overflow = previousOverflow;
      };
    }, [isMobileNavOpen]);

    const closeMobileNav = () => {
      setIsMobileNavOpen(false);
      setIsWebAppOpen(false);
      setIsMobileAppOpen(false);
      setIsEmployeeManagementOpen(false);
      setIsDatabaseSheetsOpen(false);
    };

    const handleLogout = () => {
      logoutAdmin();
      navigate("/", { replace: true });
    };

    const handleOpenSettings = () => {
      setIsProfileOpen(false);
      setIsPhotoOptionsOpen(false);
      setIsPhotoViewOpen(false);
      setCropImage("");
      navigate("/system-settings");
    };

    const handlePhotoUploadClick = () => {
      setProfilePictureError("");
      setIsPhotoOptionsOpen(false);
      profilePictureInputRef.current?.click();
    };

    const handleProfilePictureChange = (event) => {
      const file = event.target.files?.[0];
      setProfilePictureError("");

      if (!file) {
        return;
      }

      if (!file.type.startsWith("image/")) {
        setProfilePictureError("Please upload an image file.");
        return;
      }

      if (file.size > 1024 * 1024) {
        setProfilePictureError("Please upload an image under 1MB.");
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        setCropImage(reader.result);
        setCropZoom(1);
        setCropX(50);
        setCropY(50);
      };
      reader.readAsDataURL(file);
      event.target.value = "";
    };

    const handleSaveCroppedImage = () => {
      if (!cropImage) {
        return;
      }

      const image = new Image();

      image.onload = () => {
        const outputSize = 320;
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        const sourceSize = Math.min(image.naturalWidth, image.naturalHeight) / cropZoom;
        const sourceX = (image.naturalWidth - sourceSize) * (cropX / 100);
        const sourceY = (image.naturalHeight - sourceSize) * (cropY / 100);

        canvas.width = outputSize;
        canvas.height = outputSize;
        context.drawImage(image, sourceX, sourceY, sourceSize, sourceSize, 0, 0, outputSize, outputSize);

        try {
          const croppedImage = canvas.toDataURL("image/jpeg", 0.86);
          saveAdminProfilePicture(adminEmail, croppedImage);
          saveLoggedInAdminProfileImage(croppedImage);
          setProfilePicture(croppedImage);
          setCropImage("");
          setProfilePictureError("");
        } catch {
          setProfilePictureError("Unable to save this image. Try a smaller file.");
        }
      };

      image.onerror = () => {
        setProfilePictureError("Unable to crop this image. Please select another image.");
      };

      image.src = cropImage;
    };

    const handleDeleteProfilePicture = () => {
      deleteAdminProfilePicture(adminEmail);
      saveLoggedInAdminProfileImage("");
      setProfilePicture("");
      setIsPhotoOptionsOpen(false);
      setIsPhotoViewOpen(false);
      setProfilePictureError("");
    };

    return (
      <>
      <header className="app-header">
        <nav className="app-nav" aria-label="Main">
            <button
              type="button"
              className="nav-hamburger"
              aria-label={isMobileNavOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMobileNavOpen}
              aria-controls="primary-nav-links"
              onClick={() => setIsMobileNavOpen((current) => !current)}
            >
              {isMobileNavOpen ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
              )}
            </button>

            <div
              id="primary-nav-links"
              className={`nav-links${isMobileNavOpen ? " open" : ""}`}
            >
            {showDashboard && (
            <NavLink
              to="/dashboard"
              className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}
              onClick={closeMobileNav}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '8px' }}>
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                <polyline points="9 22 9 12 15 12 15 22"></polyline>
              </svg>
              Home
            </NavLink>
            )}
            {showWebApp && (
              <div className="nav-dropdown" ref={webAppMenuRef}>
                <button
                  type="button"
                  className={`nav-link nav-dropdown-toggle${isWebAppRouteActive || isWebAppOpen ? " active" : ""}`}
                  aria-haspopup="menu"
                  aria-expanded={isWebAppOpen}
                  onClick={() => {
                    setIsMobileAppOpen(false);
                    setIsEmployeeManagementOpen(false);
                    setIsDatabaseSheetsOpen(false);
                    setIsWebAppOpen((current) => !current);
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: "8px" }}>
                    <rect x="2" y="3" width="20" height="14" rx="2"></rect>
                    <line x1="8" y1="21" x2="16" y2="21"></line>
                    <line x1="12" y1="17" x2="12" y2="21"></line>
                  </svg>
                  Web App
                  <svg
                    className={`nav-dropdown-chevron${isWebAppOpen ? " open" : ""}`}
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    aria-hidden="true"
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>

                {isWebAppOpen && (
                  <div className="nav-dropdown-menu" role="menu" aria-label="Web App">
                    {showWebDashboard && (
                      <NavLink
                        to="/web-dashboard"
                        role="menuitem"
                        className={({ isActive }) => isActive ? "nav-dropdown-item active" : "nav-dropdown-item"}
                        onClick={closeMobileNav}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                          <rect x="3" y="3" width="7" height="7"></rect>
                          <rect x="14" y="3" width="7" height="7"></rect>
                          <rect x="14" y="14" width="7" height="7"></rect>
                          <rect x="3" y="14" width="7" height="7"></rect>
                        </svg>
                        Dashboard
                      </NavLink>
                    )}
                    {showClients && (
                      <NavLink
                        to="/branches"
                        role="menuitem"
                        className={({ isActive }) => isActive && !isMobileClientsDetails ? "nav-dropdown-item active" : "nav-dropdown-item"}
                        onClick={closeMobileNav}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                          <circle cx="9" cy="7" r="4"></circle>
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                        </svg>
                        Clients
                      </NavLink>
                    )}
                    {showQueries && (
                      <NavLink
                        to="/queries"
                        role="menuitem"
                        className={({ isActive }) => isActive ? "nav-dropdown-item active" : "nav-dropdown-item"}
                        style={queriesNavLinkStyle}
                        onClick={closeMobileNav}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                          <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z"></path>
                          <line x1="8" y1="9" x2="16" y2="9"></line>
                          <line x1="8" y1="13" x2="14" y2="13"></line>
                        </svg>
                        Queries
                        {pendingQueriesCount > 0 && (
                          <span style={queryBadgeStyle} aria-label={`${pendingQueriesCount} pending queries`}>
                            {pendingQueriesCount > 99 ? "99+" : pendingQueriesCount}
                          </span>
                        )}
                      </NavLink>
                    )}
                    {showTecfolkActivity && (
                    <NavLink
                      to="/tecfolk-activity"
                      role="menuitem"
                      className={({ isActive }) => isActive ? "nav-dropdown-item active" : "nav-dropdown-item"}
                      onClick={closeMobileNav}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
                      </svg>
                      Tecfolk Activity
                    </NavLink>
                    )}
                  </div>
                )}
              </div>
            )}
            {showMobileApp && (
            <div className="nav-dropdown" ref={mobileAppMenuRef}>
              <button
                type="button"
                className={`nav-link nav-dropdown-toggle${isMobileAppRouteActive || isMobileAppOpen ? " active" : ""}`}
                aria-haspopup="menu"
                aria-expanded={isMobileAppOpen}
                onClick={() => {
                  setIsWebAppOpen(false);
                  setIsEmployeeManagementOpen(false);
                  setIsDatabaseSheetsOpen(false);
                  setIsMobileAppOpen((current) => !current);
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: "8px" }}>
                  <rect x="7" y="2" width="10" height="20" rx="2" ry="2"></rect>
                  <line x1="11" y1="6" x2="13" y2="6"></line>
                  <line x1="12" y1="18" x2="12.01" y2="18"></line>
                </svg>
                Mobile App
                <svg
                  className={`nav-dropdown-chevron${isMobileAppOpen ? " open" : ""}`}
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>

              {isMobileAppOpen && (
                <div className="nav-dropdown-menu" role="menu" aria-label="Mobile App">
                  {showMobileDashboard && (
                    <NavLink
                      to="/mobile-dashboard"
                      role="menuitem"
                      className={({ isActive }) => isActive ? "nav-dropdown-item active" : "nav-dropdown-item"}
                      onClick={closeMobileNav}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <rect x="3" y="3" width="7" height="7"></rect>
                        <rect x="14" y="3" width="7" height="7"></rect>
                        <rect x="14" y="14" width="7" height="7"></rect>
                        <rect x="3" y="14" width="7" height="7"></rect>
                      </svg>
                      Dashboard
                    </NavLink>
                  )}
                  {showMobileAppClients && (
                    <NavLink
                      to="/mobile-clients"
                      role="menuitem"
                      className={({ isActive }) => isActive || isMobileClientsDetails ? "nav-dropdown-item active" : "nav-dropdown-item"}
                      onClick={closeMobileNav}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                        <circle cx="9" cy="7" r="4"></circle>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                      </svg>
                      Clients
                    </NavLink>
                  )}
                  {showFeatureRequests && (
                    <NavLink
                      to="/feature-requests"
                      role="menuitem"
                      className={({ isActive }) => isActive ? "nav-dropdown-item active" : "nav-dropdown-item"}
                      onClick={closeMobileNav}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="M9 18h6"></path>
                        <path d="M10 22h4"></path>
                        <path d="M8.5 14.5A7 7 0 1 1 15.5 14.5C14.6 15.2 14 16.2 14 17h-4c0-.8-.6-1.8-1.5-2.5Z"></path>
                      </svg>
                      Requested Feature
                    </NavLink>
                  )}
                </div>
              )}
            </div>
            )}
            {showEmployeeManagementMenu && (
              <div className="nav-dropdown" ref={employeeManagementMenuRef}>
                <button
                  type="button"
                  className={`nav-link nav-dropdown-toggle${isEmployeeManagementRouteActive || isEmployeeManagementOpen ? " active" : ""}`}
                  aria-haspopup="menu"
                  aria-expanded={isEmployeeManagementOpen}
                  onClick={() => {
                    setIsWebAppOpen(false);
                    setIsMobileAppOpen(false);
                    setIsDatabaseSheetsOpen(false);
                    setIsEmployeeManagementOpen((current) => !current);
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: "8px" }}>
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <polyline points="16 11 18 13 22 9"></polyline>
                  </svg>
                  Employee Management
                  <svg
                    className={`nav-dropdown-chevron${isEmployeeManagementOpen ? " open" : ""}`}
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    aria-hidden="true"
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>

                {isEmployeeManagementOpen && (
                  <div className="nav-dropdown-menu" role="menu" aria-label="Employee Management">
                    {showEmployeeManagement && (
                      <NavLink
                        to="/employee-management"
                        role="menuitem"
                        className={({ isActive }) => isActive ? "nav-dropdown-item active" : "nav-dropdown-item"}
                        onClick={closeMobileNav}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                          <circle cx="9" cy="7" r="4"></circle>
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                          <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                        </svg>
                        Employees
                      </NavLink>
                    )}
                    {showAppActivity && (
                      <NavLink
                        to="/app-activity"
                        role="menuitem"
                        className={({ isActive }) => isActive ? "nav-dropdown-item active" : "nav-dropdown-item"}
                        onClick={closeMobileNav}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                          <line x1="18" y1="20" x2="18" y2="10"></line>
                          <line x1="12" y1="20" x2="12" y2="4"></line>
                          <line x1="6" y1="20" x2="6" y2="14"></line>
                        </svg>
                        Employee Activity
                      </NavLink>
                    )}
                  </div>
                )}
              </div>
            )}
            {showPublished && (
              <NavLink
                to="/published"
                className={({ isActive }) => isActive ? "nav-link active" : "nav-link"}
                onClick={closeMobileNav}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: "8px" }}>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Published
              </NavLink>
            )}
            {showDatabaseSheetsMenu && (
              <div className="nav-dropdown" ref={databaseSheetsMenuRef}>
                <button
                  type="button"
                  className={`nav-link nav-dropdown-toggle${isDatabaseSheetsRouteActive || isDatabaseSheetsOpen ? " active" : ""}`}
                  aria-haspopup="menu"
                  aria-expanded={isDatabaseSheetsOpen}
                  onClick={() => {
                    setIsWebAppOpen(false);
                    setIsMobileAppOpen(false);
                    setIsEmployeeManagementOpen(false);
                    setIsDatabaseSheetsOpen((current) => !current);
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: "8px" }}>
                    <rect x="3" y="3" width="18" height="18" rx="2"></rect>
                    <line x1="3" y1="9" x2="21" y2="9"></line>
                    <line x1="3" y1="15" x2="21" y2="15"></line>
                    <line x1="9" y1="3" x2="9" y2="21"></line>
                    <line x1="15" y1="3" x2="15" y2="21"></line>
                  </svg>
                  DataBase Sheets
                  <svg
                    className={`nav-dropdown-chevron${isDatabaseSheetsOpen ? " open" : ""}`}
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    aria-hidden="true"
                  >
                    <polyline points="6 9 12 15 18 9"></polyline>
                  </svg>
                </button>

                {isDatabaseSheetsOpen && (
                  <div className="nav-dropdown-menu" role="menu" aria-label="DataBase Sheets">
                    {showVendorSheet && (
                      <NavLink
                        to="/database-sheets?tab=vendor"
                        role="menuitem"
                        className={() => {
                          const tab = new URLSearchParams(location.search).get("tab");
                          const isVendorActive = location.pathname === "/database-sheets"
                            && (!tab || tab === "vendor");
                          return isVendorActive ? "nav-dropdown-item active" : "nav-dropdown-item";
                        }}
                        onClick={closeMobileNav}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                          <rect x="3" y="3" width="18" height="18" rx="2"></rect>
                          <line x1="3" y1="9" x2="21" y2="9"></line>
                          <line x1="9" y1="3" x2="9" y2="21"></line>
                        </svg>
                        Vendor
                      </NavLink>
                    )}
                    {showTechnicianSheet && (
                      <NavLink
                        to="/database-sheets?tab=technician"
                        role="menuitem"
                        className={() => {
                          const tab = new URLSearchParams(location.search).get("tab");
                          const isTechnicianActive = location.pathname === "/database-sheets"
                            && tab === "technician";
                          return isTechnicianActive ? "nav-dropdown-item active" : "nav-dropdown-item";
                        }}
                        onClick={closeMobileNav}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                          <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
                        </svg>
                        Technician
                      </NavLink>
                    )}
                    {showContactUsSheet && (
                      <NavLink
                        to="/database-sheets?tab=contact-us"
                        role="menuitem"
                        className={() => {
                          const tab = new URLSearchParams(location.search).get("tab");
                          const isContactUsActive = (
                            location.pathname === "/database-sheets" && tab === "contact-us"
                          ) || location.pathname === "/contact-us";
                          return isContactUsActive ? "nav-dropdown-item active" : "nav-dropdown-item";
                        }}
                        onClick={closeMobileNav}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                        </svg>
                        Contact Us
                      </NavLink>
                    )}
                    {showLoginLogsSheet && (
                      <NavLink
                        to="/database-sheets?tab=login-logs"
                        role="menuitem"
                        className={() => {
                          const tab = new URLSearchParams(location.search).get("tab");
                          const isLoginLogsActive = (
                            location.pathname === "/database-sheets" && tab === "login-logs"
                          ) || location.pathname === "/login-logs";
                          return isLoginLogsActive ? "nav-dropdown-item active" : "nav-dropdown-item";
                        }}
                        onClick={closeMobileNav}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                          <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
                          <polyline points="10 17 15 12 10 7"></polyline>
                          <line x1="15" y1="12" x2="3" y2="12"></line>
                        </svg>
                        Login Logs
                      </NavLink>
                    )}
                  </div>
                )}
              </div>
            )}
            </div>
            <div className="profile-menu" ref={profileMenuRef}>
              <button
                type="button"
                className={`profile-button${isProfileOpen ? " active" : ""}`}
                onClick={() => {
                  closeMobileNav();
                  setIsProfileOpen((current) => !current);
                }}
                aria-haspopup="dialog"
                aria-expanded={isProfileOpen}
              >
                {profilePicture ? (
                  <img className="profile-avatar profile-picture" src={profilePicture} alt="Admin profile" />
                ) : (
                  <span className="profile-avatar">{avatarInitial}</span>
                )}
                <span className="profile-button-label">Login Profile</span>
              </button>

              {!isProfileOpen && (
                <article className="home-user-card profile-hover-card" aria-hidden="true">
                  <div className="home-user-avatar-wrap">
                    {profilePicture ? (
                      <img className="home-user-avatar" src={profilePicture} alt="" />
                    ) : (
                      <span className="home-user-avatar">{avatarInitial}</span>
                    )}
                  </div>
                  <div className="home-user-meta">
                    <p className="home-user-status">
                      <span className="home-user-dot" aria-hidden="true" />
                      Signed in
                    </p>
                    <strong>{adminName}</strong>
                    <div className="home-user-details">
                      {adminDesignation ? <span className="home-user-role">{adminDesignation}</span> : null}
                      {adminEmail ? <span className="home-user-login">{adminEmail}</span> : null}
                    </div>
                  </div>
                </article>
              )}

              {isProfileOpen && (
                <div className="profile-popup" role="dialog" aria-label="Admin login profile">
                  <button
                    type="button"
                    className="profile-settings-button"
                    onClick={handleOpenSettings}
                    aria-label="Open settings"
                    title="Settings"
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <circle cx="12" cy="12" r="3"></circle>
                      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06A1.65 1.65 0 0 0 15 19.4a1.65 1.65 0 0 0-1 .6V20a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-.6 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-.6-1H4a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 .6-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-.6V4a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 .6 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9c.2.34.4.68.6 1H20a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-.51 1Z"></path>
                    </svg>
                  </button>
                  <div className="profile-popup-header">
                    {profilePicture ? (
                      <img className="profile-avatar profile-avatar-large profile-picture" src={profilePicture} alt="Admin profile" />
                    ) : (
                      <span className="profile-avatar profile-avatar-large">{avatarInitial}</span>
                    )}
                    <button
                      type="button"
                      className="profile-photo-button"
                      onClick={() => setIsPhotoOptionsOpen((current) => !current)}
                    >
                      Click
                    </button>
                  </div>

                  <div className="profile-photo-section">
                    {isPhotoOptionsOpen && (
                      <div className="profile-photo-options" role="dialog" aria-label="Profile picture options">
                        {profilePicture ? (
                          <>
                            <button type="button" onClick={() => setIsPhotoViewOpen(true)}>
                              View
                            </button>
                            <button type="button" onClick={handlePhotoUploadClick}>
                              Change
                            </button>
                            <button type="button" className="danger" onClick={handleDeleteProfilePicture}>
                              Delete
                            </button>
                          </>
                        ) : (
                          <button type="button" onClick={handlePhotoUploadClick}>
                            Upload
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  <input
                    ref={profilePictureInputRef}
                    id="profile-picture"
                    className="profile-picture-file-input"
                    type="file"
                    accept="image/*"
                    onChange={handleProfilePictureChange}
                  />
                  {profilePictureError && <div className="profile-picture-error">{profilePictureError}</div>}

                  <div className="profile-info-card">
                    <label htmlFor="profile-name">Name</label>
                    <input id="profile-name" type="text" value={adminName} readOnly />

                    <label htmlFor="profile-designation">Designation</label>
                    <input
                      id="profile-designation"
                      className="profile-designation-input"
                      type="text"
                      value={adminDesignation}
                      readOnly
                    />
                  </div>

                  <div className="profile-credentials-section">
                    <span className="profile-credentials-heading">Login Credentials</span>

                  <label htmlFor="profile-username">Username</label>
                  <input id="profile-username" type="text" value={adminEmail} readOnly />

                  <label htmlFor="profile-password">Password</label>
                  <div className="profile-password-field">
                    <input
                      id="profile-password"
                      type={isPasswordVisible ? "text" : "password"}
                      value={isPasswordVisible ? adminPassword || "Password not available" : "********"}
                      readOnly
                    />
                    <button
                      type="button"
                      className="profile-password-toggle"
                      onClick={() => setIsPasswordVisible((current) => !current)}
                      aria-label={isPasswordVisible ? "Hide password" : "Show password"}
                    >
                      {isPasswordVisible ? (
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M2 4.27 4.28 2 22 19.72 19.73 22l-3.1-3.1A11.48 11.48 0 0 1 12 20C5 20 1 12 1 12a19.52 19.52 0 0 1 5-5.95L2 4.27Zm7.53 7.53a2.5 2.5 0 0 0 2.67 2.67L9.53 11.8ZM12 4c7 0 11 8 11 8a18.74 18.74 0 0 1-3.38 4.55l-3.2-3.2A4.93 4.93 0 0 0 17 12a5 5 0 0 0-6.35-4.8L8.17 4.72A11.26 11.26 0 0 1 12 4Z" />
                        </svg>
                      ) : (
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M12 4C5 4 1 12 1 12s4 8 11 8 11-8 11-8-4-8-11-8Zm0 13a5 5 0 1 1 0-10 5 5 0 0 1 0 10Zm0-2.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />
                        </svg>
                      )}
                    </button>
                  </div>
                  </div>

                  <button type="button" className="profile-logout-button" onClick={handleLogout}>
                    Logout
                  </button>

                  {isPhotoViewOpen && (
                    <div className="profile-modal-backdrop" role="dialog" aria-label="View profile picture">
                      <div className="profile-modal-card profile-view-card">
                        <h3>Profile Picture</h3>
                        <img className="profile-view-image" src={profilePicture} alt="Admin profile large preview" />
                        <button type="button" className="profile-modal-close" onClick={() => setIsPhotoViewOpen(false)}>
                          Close
                        </button>
                      </div>
                    </div>
                  )}

                  {cropImage && (
                    <div className="profile-modal-backdrop" role="dialog" aria-label="Crop profile picture">
                      <div className="profile-modal-card profile-crop-card">
                        <h3>Crop Profile Picture</h3>
                        <div className="profile-crop-preview">
                          <img
                            src={cropImage}
                            alt="Selected profile crop preview"
                            style={{
                              objectPosition: `${cropX}% ${cropY}%`,
                              transform: `scale(${cropZoom})`,
                              transformOrigin: `${cropX}% ${cropY}%`
                            }}
                          />
                        </div>

                        <label htmlFor="profile-crop-zoom">Zoom</label>
                        <input
                          id="profile-crop-zoom"
                          type="range"
                          min="1"
                          max="3"
                          step="0.05"
                          value={cropZoom}
                          onChange={(event) => setCropZoom(Number(event.target.value))}
                        />

                        <label htmlFor="profile-crop-x">Move Left / Right</label>
                        <input
                          id="profile-crop-x"
                          type="range"
                          min="0"
                          max="100"
                          value={cropX}
                          onChange={(event) => setCropX(Number(event.target.value))}
                        />

                        <label htmlFor="profile-crop-y">Move Up / Down</label>
                        <input
                          id="profile-crop-y"
                          type="range"
                          min="0"
                          max="100"
                          value={cropY}
                          onChange={(event) => setCropY(Number(event.target.value))}
                        />

                        <div className="profile-modal-actions">
                          <button type="button" className="profile-modal-close" onClick={() => setCropImage("")}>
                            Cancel
                          </button>
                          <button type="button" className="profile-modal-save" onClick={handleSaveCroppedImage}>
                            Save
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
        </nav>
        {isMobileNavOpen && (
          <div className="nav-backdrop" onClick={closeMobileNav} aria-hidden="true" />
        )}
      </header>
      {queryNotification && (
        <div
          className="query-toast"
          role="button"
          tabIndex={0}
          aria-live="polite"
          onClick={openQueriesPage}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              openQueriesPage();
            }
          }}
        >
          <strong style={queryPopupHeadingStyle}>Query</strong>
          <span style={queryPopupTextStyle}>{queryNotification}</span>
        </div>
      )}
      {licenseNotification?.body && (
        <div
          className="query-toast"
          style={{ top: queryNotification ? 250 : 82 }}
          role="button"
          tabIndex={0}
          aria-live="polite"
          onClick={() => openLicenseRequest(licenseNotification.branchId)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              openLicenseRequest(licenseNotification.branchId);
            }
          }}
        >
          <strong style={queryPopupHeadingStyle}>License Update Request</strong>
          <span style={queryPopupTextStyle}>{licenseNotification.body}</span>
        </div>
      )}
      </>
    );
  }

function playQueryNotificationTone() {
  try {
    const AudioContextConstructor = window.AudioContext || window.webkitAudioContext;

    if (!AudioContextConstructor) {
      return;
    }

    const audioContext = new AudioContextConstructor();
    const startedAt = audioContext.currentTime;
    const gainNode = audioContext.createGain();

    gainNode.connect(audioContext.destination);
    gainNode.gain.setValueAtTime(0.0001, startedAt);
    gainNode.gain.exponentialRampToValueAtTime(0.18, startedAt + 0.03);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, startedAt + 0.5);

    [880, 1175].forEach((frequency, index) => {
      const oscillator = audioContext.createOscillator();
      const toneStartAt = startedAt + (index * 0.16);

      oscillator.type = "sine";
      oscillator.frequency.setValueAtTime(frequency, toneStartAt);
      oscillator.connect(gainNode);
      oscillator.start(toneStartAt);
      oscillator.stop(toneStartAt + 0.18);
    });

    if (audioContext.state === "suspended") {
      audioContext.resume().catch(() => {});
    }

    window.setTimeout(() => {
      audioContext.close().catch(() => {});
    }, 800);
  } catch {
    // Some browsers block notification audio until the user interacts with the page.
  }
}

function extractQueryItems(response) {
  if (Array.isArray(response)) {
    return response;
  }

  if (!response || typeof response !== "object") {
    return [];
  }

  const listKeys = [
    "queries",
    "query",
    "complaints",
    "complaint",
    "enquiries",
    "enquiry",
    "inquiries",
    "inquiry",
    "response",
    "data",
    "result",
    "results",
    "records",
    "items",
    "docs"
  ];

  for (const key of listKeys) {
    const value = response[key];

    if (Array.isArray(value)) {
      return value;
    }

    const nestedItems = extractQueryItems(value);
    if (nestedItems.length > 0) {
      return nestedItems;
    }
  }

  return [];
}

function isPendingQuery(query) {
  const status = getValue(query, ["status", "complaintStatus", "queryStatus", "state"]) || "Pending";
  return getQueryStatusKey(status) === "pending";
}

function getQueryKey(query) {
  const id = getValue(query, ["_id", "id", "complaintId", "queryId", "ticketNumber"]);

  if (id) {
    return String(id);
  }

  return [
    getValue(query, ["createdAt", "created_at", "complaintDate", "queryDate", "date", "submittedAt", "updatedAt"]),
    getValue(query, ["message", "complaint", "complaintText", "complaintDescription", "query", "question", "description", "details", "remarks", "comment", "subject"]),
    getValue(query, ["branchId", "branch", "branch_id", "gymBranchId", "clientBranchId"])
  ].map(formatQueryKeyPart).join("|");
}

function getValue(source, keys) {
  if (!source || typeof source !== "object") {
    return "";
  }

  for (const key of keys) {
    if (source[key] !== undefined && source[key] !== null && source[key] !== "") {
      return source[key];
    }
  }

  return "";
}

function getQueryStatusKey(status) {
  return String(status || "").trim().toLowerCase().replace(/[\s_-]+/g, "");
}

function formatQueryKeyPart(value) {
  if (!value) {
    return "";
  }

  if (typeof value === "object") {
    return value._id || value.id || value.name || JSON.stringify(value);
  }

  return String(value);
}

function getStoredPendingQueryKeys() {
  try {
    const storedKeys = window.sessionStorage.getItem(PENDING_QUERY_STORAGE_KEY);

    if (!storedKeys) {
      return null;
    }

    const parsedKeys = JSON.parse(storedKeys);
    return Array.isArray(parsedKeys) ? parsedKeys.map(String) : null;
  } catch {
    return null;
  }
}

function savePendingQueryKeys(queryKeys) {
  try {
    window.sessionStorage.setItem(PENDING_QUERY_STORAGE_KEY, JSON.stringify(queryKeys));
  } catch {
    // Ignore storage failures; the badge still updates for the current render.
  }
}

const queriesNavLinkStyle = {
  gap: "0"
};

const queryBadgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  minWidth: "22px",
  height: "22px",
  marginLeft: "8px",
  padding: "0 6px",
  borderRadius: "999px",
  backgroundColor: "#ef4444",
  color: "#fff",
  fontSize: "12px",
  fontWeight: 800,
  lineHeight: 1
};

const queryPopupHeadingStyle = {
  color: "#4ade80",
  fontSize: "21px",
  fontWeight: 800,
  lineHeight: 1.2
};

const queryPopupTextStyle = {
  fontSize: "17px",
  fontWeight: 600,
  lineHeight: 1.35
};

  export default Header;
